import { useSelector } from 'react-redux';
import { getFollowupPaymentInfo } from '~/api/order.js';
import { ORDER_STATE } from '~/constant';
import { AppsflyerPBA } from '~/third_party/Appsflyer.jsx';
import {
  getDriverLocation as callGetDriverLocation,
  getOrders as callGetOrders,
  postDriverReview,
  postOrderReview,
  putCancelOrder,
} from '../../../api/order.js';
import { extract } from '../../../utils/jsonApi.js';
import { orderTypeToServiceType } from '../selector/orderSelectors.js';
import { selectUserServiceRegionId } from '../selector/userSelectors.js';
import { openAlert } from './alertActions.js';

const BASE = 'orders :';

export const orderActionTypes = {
  getOrdersStart: `${BASE} fetch start`,
  getOrdersSuccess: `${BASE} fetch SUCCESS`,
  getOrdersFailed: `${BASE} fetch _FAILED`,

  cancelOrderStart: `${BASE} cancel : persist start`,
  cancelOrderSuccess: `${BASE} cancel : persist SUCCESS`,
  cancelOrderFailed: `${BASE} cancel : persist FAILED`,

  reviewOrderStart: `${BASE} review : persist start`,
  reviewOrderSuccess: `${BASE} review : persist SUCCESS`,
  reviewOrderFailed: `${BASE} review : persist FAILED`,

  reviewDriverStart: `${BASE} review : persist start`,
  reviewDriverSuccess: `${BASE} review : persist SUCCESS`,
  reviewDriverFailed: `${BASE} review : persist FAILED`,

  getMealsStart: `${BASE} meals : fetch start`,
  getMealsSuccess: `${BASE} meals : fetch SUCCESS`,
  getMealsFailed: `${BASE} meals : fetch FAILED`,

  getDriverLocation: `${BASE} driver location : fetch start`,
  getDriverLocationSuccess: `${BASE} driver location : fetch SUCCESS`,
  getDriverLocationFailed: `${BASE} driver location : fetch FAILED`,

  setPaymentInfo: `${BASE} set payment info`,
};

/**
 * Get all the orders and kick of lookups of related information
 * such as payment info and driver assignments
 * @param {*} page
 */
export const getOrders = (page) => async (dispatch, getState) => {
  const { order } = getState();
  if (order.isLoading) {
    return;
  }
  try {
    dispatch({ type: orderActionTypes.getOrdersStart });
    const data = await callGetOrders(page);
    const orders = extract(data, 'order') || [];
    const ordersByOrderNumber = orders.reduce((acc, order) => {
      acc[order.attributes.number] = order;
      return acc;
    }, {});
    getPaymentInfoForUnauthorizedOrders(dispatch, orders);
    dispatch({
      type: orderActionTypes.getOrdersSuccess,
      orders: ordersByOrderNumber,
    });
  } catch (e) {
    dispatch({
      type: orderActionTypes.getOrdersFailed,
    });
    dispatch(
      openAlert({
        variant: 'error',
        message: 'Failed to retrieve orders',
      })
    );
  }
};

/**
 * For any order that is unauthorized, look up the payment information.
 * this is important because we handle Alipay and Stripe payments differently in the UI.
 * @param {*} dispatch
 * @param {*} orders
 */
const getPaymentInfoForUnauthorizedOrders = (dispatch, orders) => {
  orders.forEach(async (order) => {
    if (order.attributes.state === ORDER_STATE.UNAUTHORIZED) {
      const followupPaymentInfo = await getFollowupPaymentInfo(order.attributes.paymentUri);
      // flatten paymentInfo for easy access
      const paymentInfo = {
        id: followupPaymentInfo.id,
        type: followupPaymentInfo.type,
        ...followupPaymentInfo.attributes,
      };
      dispatch({
        type: orderActionTypes.setPaymentInfo,
        orderNumber: order.attributes.number,
        paymentInfo,
      });
    }
  });
};

export const getDriverLocation = (driverId) => async (dispatch) => {
  try {
    dispatch({
      type: orderActionTypes.getDriverLocation,
    });
    const data = await callGetDriverLocation(driverId);
    dispatch({
      type: orderActionTypes.getDriverLocationSuccess,
      driver: {
        [driverId]: {
          location: data,
        },
      },
    });
  } catch (e) {
    dispatch({
      type: orderActionTypes.getDriverLocationFailed,
    });
  }
};

export const cancelOrder = (order) => async (dispatch, getState) => {
  const serviceRegionId = selectUserServiceRegionId(getState());
  const {
    attributes: { number, amounts },
    relationships: { amountDeductions },
  } = order;
  let promoCode = "";
  let promoCodeAppliedAmount = 0;
  let serviceType = orderTypeToServiceType(order.attributes.type);
  let promoCodeDeduction = (amountDeductions?.data || []).find((amountDeduction) => {
    let { attributes } = amountDeduction;
    if (attributes["deductionSource"] = "promo" && attributes["description"].match(/Promo Code \((.+)\)/)) {
      return true;
    }
    return false;
  });
  if (promoCodeDeduction) {
    promoCodeAppliedAmount = parseFloat(promoCodeDeduction.attributes.amountApplied) || 0;
    promoCode = (promoCodeDeduction.attributes.description.match(/Promo Code \((.+)\)/)[1]) || 0;
  }
  try {
    dispatch({
      type: orderActionTypes.cancelOrderStart,
      order,
    });
    await putCancelOrder(number);
    AppsflyerPBA.trackWebOrderCancelled({
      revenue: amounts.find((amount) => amount.name === 'total')?.amount || 0,
      order_id: number,
      promo_code: promoCode || '',
      promo_code_amount: promoCodeAppliedAmount,
      service_region: serviceRegionId,
      order_type: serviceType,
      currency: 'USD',
    });
    dispatch(
      openAlert({
        message: 'Your order has been cancelled.',
        variant: 'success',
      })
    );
    order.attributes.state = ORDER_STATE.REFUNDED;
    dispatch({
      type: orderActionTypes.cancelOrderSuccess,
      order,
    });
  } catch (e) {
    dispatch(
      openAlert({
        message: 'Failed to cancel your order.',
        variant: 'error',
      })
    );
    dispatch({
      type: orderActionTypes.cancelOrderFailed,
    });
  }
};

export const reviewOrderMeals = (mealsReviews) => async (dispatch) => {
  dispatch({
    type: orderActionTypes.reviewOrderStart,
  });
  try {
    await postOrderReview(mealsReviews);
    dispatch({
      type: orderActionTypes.reviewOrderSuccess,
      mealsReviews,
    });
  } catch (e) {
    dispatch({
      type: orderActionTypes.reviewOrderFailed,
    });
  }
};

export const reviewDriver = ({ driverId, orderNumber, rate, comment }) => async (dispatch) => {
  dispatch({
    type: orderActionTypes.reviewDriverStart,
  });
  try {
    await postDriverReview({
      driverId,
      orderNumber,
      rate,
      content: comment,
    });
    dispatch({
      type: orderActionTypes.reviewDriverSuccess,
      orderNumber,
    });
  } catch (e) {
    dispatch({
      type: orderActionTypes.reviewOrderFailed,
    });
  }
};
